import React from 'react'
import '../css/basemap-widget.css'
import '../css/scalebar-widget.css'
import styled from 'styled-components'
import MapLoadingSpinner from '../components/map-loading-spinner'
import IEventedComponent, {
  messageTypes
} from '../components/IEventedComponent'
import { SceneChangedEvent,MaskChangedEvent } from '../lib/messages'
import mapConfig from '../config/region-map-config.json'
import MapOverlayControl from '../controls/map-overlay-control'

declare global {
  interface Window {
    require: (items: string[], callback: (...items: any) => void) => void
  }
}
const MapDiv = styled.div`
  width: 100%;
  height: 100%;
`
interface IProps {
  region: string
  regionTitle: string
}

const MapControl = class extends IEventedComponent<IProps, {}> { 
  map: any
  mapView: any
  sr: any
  maskLayer: any
  contentLayer: any
  activeSceneIndex: number = 0
  activeMaskIndex: number = 0
  sceneExtents: any
  maskExtents: any
  initialExtent: any
  activeSceneExtent: any
  activeMaskExtent:any
  maskExtentArray: any[] = []
  activeSceneZoom: any
  maskGeometry: any
  prevExtent: any
  performMaskContains: boolean = true
  mapViewUpdatingWatchHandle: any
  mapSpinnerTimeout: any
  featuresFound: boolean = true

  state = {
    mapViewUpdating: false,
    featuresFound: true
  }

  componentDidMount() {
    this.initMap()
    this.subscribe(
      messageTypes.onSceneChanged,
      this.handleActivateScene.bind(this)
    )
    this.subscribe(
      messageTypes.onMaskChanged,
      this.handleActivateMask.bind(this)
    )
    
     document.addEventListener('touchstart', function (event) {
      if (event.touches.length > 1) {
        event.preventDefault(); 
      }
    }, false);
  }

  componentDidUpdate(prevProps: any) {
 
    if (
      prevProps.region !== this.props.region &&
      this.props.region !== 'unknown'
    ) {
      console.log(prevProps);
      this.initRegionLayers()
    }
  }

  componentWillUnmount = () => {
    this.mapViewUpdatingWatchHandle.remove()
    clearTimeout(this.mapSpinnerTimeout)
  }

  handleActivateMask(message: MaskChangedEvent) {
     this.activeMaskIndex = message.maskId;
     this.updateMaskLayers(this.activeMaskIndex);
     this.updateMaskExtent(this.activeMaskIndex);
  }

  handleActivateScene(message: SceneChangedEvent) {
  
    this.activeSceneIndex = message.sceneIndex
    this.updateSceneLayers(message.sceneIndex)
    this.updateSceneExtent(message.sceneIndex)
  }

  initMap = () => {
    let component = this
    window.require(
      [
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/TileLayer',
        'esri/geometry/SpatialReference',
        'esri/geometry/Extent'
      ],
      function (
        Map: any,
        MapView: any,
        TileLayer: any,
        SpatialReference: any,
        Extent: any
      ) {
        
        const baseMapLayer = new TileLayer({
          url: mapConfig.baseMapUrl
        })
        component.map = new Map({
          layers: [baseMapLayer]
        })
        component.sr = new SpatialReference({ wkid: 3111 })
        component.mapView = new MapView({
          container: 'map',
          map: component.map,
          zoom: 5,
          isPinchZoom:true,
          spatialReference: component.sr,
          extent: new Extent({
            xmin: 2040678,
            ymin: 2230751,
            xmax: 2990649,
            ymax: 2849810,
            spatialReference: component.sr
          }),
          constraints: {
            snapToZoom: false,
            rotationEnabled:true
          }
        })

        component.addMapViewUiComponents()        
        component.mapView.when(component.limitMapToSceneExtentOrRegionMask)

        component.mapViewUpdatingWatchHandle = component.mapView.watch(
          'updating',
          (updating: boolean) => {
            if (!updating) {
              // When layers update, often the spinner gets displayed and hidden quickly twice which is distracting
              // This timeout should prevent this occurring.
              component.mapSpinnerTimeout = setTimeout(() => {
                component.setState((state: any, props: any) => ({
                  mapViewUpdating: state.mapViewUpdating && updating
                }))
              }, 200)
            } else {
           
              component.setState({
                mapViewUpdating: updating
              })
  
               //query featureservice to get feature count when map view is updated
               component.getFeatureCount()
              
            }
          }
        )

        if (!!component.props.region) {
          component.initRegionLayers()
        }
      }
    )
  }

  addMapViewUiComponents = () => {
    let component = this

    window.require(['esri/widgets/ScaleBar'], function (ScaleBar: any) {
      component.mapView.ui.add(
        new ScaleBar({
          view: component.mapView,
          unit: 'metric'
        }),
        {
          position: 'bottom-right'
        }
      )
    })
  }

  initRegionLayers = () => {

    let component = this
    window.require(['esri/layers/MapImageLayer'], function (
      MapImageLayer: any
    ) {
      const config = (mapConfig.regions as any)[component.props.region]

      if (!config) {
        console.log(`no map configuration for ${component.props.region}`)
        return
      }

      const mapServicePath: string = config.mapServicePath
      const mapServiceUrl: string = `${mapConfig.arcgisServerBaseUrl}${mapServicePath}`
      const featureServiceUrl: string = config.featureServicePath
      const maskSubLayerIds: number[] = config.maskLayerIds
      const maskOutlines: number[] = config.maskOutlines
      const contentSubLayerIds: number[] = config.initialContentIds
      const initialVisbileContentIds: number[] = config.initialVisibleContentIds
      const initialMaskIds: number[] = config.initialMaskIds
      const extentSubLayerId: number = config.extentLayerId
     

      maskOutlines.forEach((maskOutline: any) => {
        component.populateActiveMaskExtents(`${mapServiceUrl}/${maskOutline}`)
      })

      component.getMaskFeatures(`${mapServiceUrl}/${maskSubLayerIds[0]}`)
      if (!!component.maskLayer) {
        component.map.layers.remove(component.maskLayer)
      }
      if (!!component.contentLayer) {
        component.map.layers.remove(component.contentLayer)
      }

      component.maskLayer = new MapImageLayer({
        url: mapServiceUrl,
        sublayers: maskSubLayerIds.map(layerId => {
              if(initialMaskIds.includes(layerId)){
          return { id: layerId, visible: true }
              }
                else{
          return { id: layerId, visible: false }
          }
        })
        ,
        visible: true
      })

      component.contentLayer = new MapImageLayer({
       url: mapServiceUrl,
       sublayers: contentSubLayerIds.map(clayerId => {
           if(initialVisbileContentIds.includes(clayerId)){

            return { id: clayerId, visible: true }
          }
          else{
              return { id: clayerId, visible: false }
          }
      }),
        visible: true 
      })

      // Remove content and mask sub layers, remaining sublayers are content to be filtered based on scene index
      component.contentLayer.when((contentLayer: any) => {
        let nonContentSubLayerIds: number[] = [
          extentSubLayerId,
          ...maskSubLayerIds
        ]
        nonContentSubLayerIds.forEach((sublayerId: number) => {
         contentLayer.allSublayers.remove(
           contentLayer.findSublayerById(sublayerId)
         )
        })
      })

      component.map.layers.addMany([
        component.maskLayer,
        component.contentLayer
      ])
    })
  }

  populateMaskExtentsError = (error: any) => {
    console.log(error)
  }

  populateMaskExtentsSuccess = (results: any) => {

    let maskNames = []
    let masks = (mapConfig.regions as any)[this.props.region].masks

    for (let i = 0; i < masks.length; i++) {
        maskNames.push(masks[i][1]) 
    }
   
 if(maskNames.includes(results.displayFieldName)){
  
    this.maskExtentArray.push(results.displayFieldName,results.features[0].geometry.extent)
      }
    this.activeMaskExtent = results.features[0].geometry.extent
  }

  getFeatureCount = () => {  
    
     const fServiceUrl = (mapConfig.regions as any)[this.props.region].featureServicePath

     let component = this
    window.require(
      ['esri/tasks/QueryTask', 'esri/tasks/support/Query'],
      function (QueryTask: any, Query: any) {
        let queryTask = new QueryTask({
          url: fServiceUrl+"/0"
        })
        let query = new Query()
          query.geometry = component.mapView.extent;  
          query.returnGeometry = true;     
          query.where = '1=1'
         queryTask.executeForCount(query).then(function(results){
 
    if(results === 0){
       component.setState((state: any, props: any) => ({
                  featuresFound: false
                }))
    }
    else{
        component.setState((state: any, props: any) => ({
                  featuresFound: true
                }))
    }
  });
       
      }
    )
  
  }

    populateActiveMaskExtents = (extentLayerUrl: string) => {
   
    let component = this
    window.require(
      ['esri/tasks/QueryTask', 'esri/tasks/support/Query'],
      function (QueryTask: any, Query: any) {
        let queryTask = new QueryTask({
          url: extentLayerUrl
        })
        let query = new Query()
        query.returnGeometry = true
        query.where = '1=1'
        queryTask
          .execute(query)
          .then(
            component.populateMaskExtentsSuccess,
            component.populateMaskExtentsError
          )
      }
    )
  }

  getMaskFeaturesSuccess = (results: any) => {
    if (results.features.length !== 1) {
      throw new Error(
        `Expected only one mask feature for region ${this.props.region}, received ${results.features.count}`
      )
    }
    this.maskGeometry = results.features[0].geometry
  }

  getMaskFeaturesError = (error: any) => {
    console.log(error)
  }

  getMaskFeatures = (maskLayerUrl: string) => {
    let component = this
    window.require(
      ['esri/tasks/QueryTask', 'esri/tasks/support/Query'],
      function (QueryTask: any, Query: any) {
        let queryTask = new QueryTask({
          url: maskLayerUrl
        })
        let query = new Query()
        query.returnGeometry = true
        query.where = '1=1'

        queryTask
          .execute(query)
          .then(
            component.getMaskFeaturesSuccess,
            component.getMaskFeaturesError
          )
      }
    )
  }

  resetSceneConstraints = () => {
    if (this.mapView && this.mapView.constraints) {
      this.mapView.constraints.minScale = 0
      this.mapView.constraints.maxScale = 0
    }
  }

  updateSceneConstraints = (sceneIndex: number) => {
    const sceneConstraints = (mapConfig.regions as any)[this.props.region].sceneConstraints
    if (sceneConstraints !== undefined && sceneConstraints.length > 0) {
      this.resetSceneConstraints()
      sceneConstraints.forEach((sceneConstraint: any) => {
        if (
          sceneIndex >= sceneConstraint.sceneOn &&
          sceneIndex <= sceneConstraint.sceneOff
        ) {
          this.mapView.constraints.maxScale =
            sceneConstraint.maxScale !== undefined
              ? sceneConstraint.maxScale
              : 0
          this.mapView.constraints.minScale =
            sceneConstraint.minScale !== undefined
              ? sceneConstraint.minScale
              : 0
        }
      })
    } else {
      this.resetSceneConstraints()
    }
  }

   updateMaskLayers = (maskIndex: number) => {
      if (!this.maskLayer) {
        return
      }

    const maskOutline = maskIndex +1;
     
    this.maskLayer.allSublayers.forEach((subLayer: any,index:any) => {

     if(subLayer.id == maskIndex || subLayer.id == maskOutline){
      subLayer.visible = true
     }
    
    // region maskLayers
    else if([3,4,5,6,7,8,9,10,11,12,13,14].includes(subLayer.id)){
    subLayer.visible = false
     }
    })
  }
  
  updateSceneLayers = (sceneIndex: number) => {

     if(!this.contentLayer){
       return
     } 
    
    //show only planned burns first two scenes
   if (sceneIndex === 10 || sceneIndex === 20) {
      console.log(sceneIndex);

      this.contentLayer.allSublayers.forEach((subLayer: any) => {
  
      subLayer.definitionExpression =  `${500} > ${
            (mapConfig.regions as any)[this.props.region].dataOffField
          }`
          subLayer.visible = true     
      })
       return
    }
 
     
// show burns, strategic breaks and mechanical layers on last scene
if(sceneIndex === 1000){

   this.contentLayer.allSublayers.forEach((subLayer: any) => {

     if(![3,4,5,6,7,8,9,10,11,12,13,14].includes(subLayer.id)){

 subLayer.definitionExpression = `${800} > ${
        (mapConfig.regions as any)[this.props.region].dataOffField
      }`
       subLayer.visible = true
      }
    })
    
}

else{
    this.contentLayer.allSublayers.forEach((subLayer: any) => {

      if(![3,4,5,6,7,8,9,10,11,12,13,14].includes(subLayer.id)){

 subLayer.definitionExpression = `${sceneIndex} >= ${
        (mapConfig.regions as any)[this.props.region].dataOnField
      } and ${sceneIndex} < ${
        (mapConfig.regions as any)[this.props.region].dataOffField
      }`
       subLayer.visible = true
      }
    })
  }
  }

   updateMaskExtent = (maskIndex: number) => {

    let masks = (mapConfig.regions as any)[this.props.region].masks
     for (let i = 0; i < masks.length; i++) {
        var innerArrayLength = masks[i].length;
        if(masks[i][0] == maskIndex){
          this.zoomToActiveMaskExtent(this.maskExtentArray[this.maskExtentArray.findIndex((e:any) => e === masks[i][1])+1])
        }
     }
  }

  updateSceneExtent = (sceneIndex: number) => {
    if (!this.sceneExtents) {
      return
    }

    let matchingExtents = this.sceneExtents.filter((sceneExtent: any) => {
      return (
        sceneIndex >=
          sceneExtent.attributes[
            (mapConfig.regions as any)[this.props.region].dataOnField
          ] &&
        sceneIndex <
          sceneExtent.attributes[
            (mapConfig.regions as any)[this.props.region].dataOffField
          ]
      )
    })
    if (!!matchingExtents.length) {
      this.zoomToActiveSceneExtent(matchingExtents[0].geometry.extent)
    } else if (
      sceneIndex < (mapConfig.regions as any)[this.props.region].initialExtentDataOnValue
    ) {
      this.zoomToActiveSceneExtent(this.initialExtent)
    }
  }

  zoomToActiveMaskExtent = (extent: any) => {
    this.activeMaskExtent = extent
    this.mapView
      .goTo(this.activeMaskExtent, { duration: 500 })
  
  }

  zoomToActiveSceneExtent = (extent: any) => {
    this.activeSceneExtent = extent
    this.mapView
      .goTo(this.activeSceneExtent, { duration: 500 })
      .then(this.limitMapToSceneExtentOrRegionMask)
  }

  zoomToIntialExtent = (extent: any) => {
    this.mapView
      .goTo(extent, { duration: 500 })
  }

  limitMapToSceneExtentOrRegionMask = () => {
    let component = this

    // Disable shift/ctrl and draw box zoom as it does not respect map constraints.
    component.mapView.on('drag', ['Shift'], (evt: any) => evt.stopPropagation())
    component.mapView.on('drag', ['Shift', 'Control'], (evt: any) =>
      evt.stopPropagation()
    )

    window.require(['esri/geometry/geometryEngine'], function (
      geometryEngine: any
    ) {
      component.activeSceneZoom = component.mapView.zoom
      component.mapView.watch('stationary', (event: any) => {
        if (
          !event ||
          !component.activeSceneExtent ||
          !component.maskGeometry ||
          !component.mapView.extent
        ) {
          return
        }
        let currentCenter = component.mapView.extent.center

        if (
          component.mapView.zoom < component.activeSceneZoom ||
          (!component.activeSceneExtent.contains(currentCenter) &&
            component.mapView.zoom === component.activeSceneZoom)
        ) {
          component.mapView.goTo(component.activeSceneExtent)
        } else if (component.performMaskContains) {
          if (
            geometryEngine.contains(
              component.maskGeometry,
              component.mapView.extent
            )
          ) {
            component.mapView.goTo(component.prevExtent)
          }
          component.performMaskContains = false
        } else if (!component.activeSceneExtent.contains(currentCenter)) {
          let revisedCenter = component.mapView.extent.center
          if (currentCenter.x < component.activeSceneExtent.xmin) {
            revisedCenter.x = component.activeSceneExtent.xmin
          }
          if (currentCenter.x > component.activeSceneExtent.xmax) {
            revisedCenter.x = component.activeSceneExtent.xmax
          }
          if (currentCenter.y < component.activeSceneExtent.ymin) {
            revisedCenter.y = component.activeSceneExtent.ymin
          }
          if (currentCenter.y > component.activeSceneExtent.ymax) {
            revisedCenter.y = component.activeSceneExtent.ymax
          }
          component.mapView.goTo(revisedCenter).then(() => {
            component.prevExtent = component.mapView.extent
          })
        } else {
          component.prevExtent = component.mapView.extent
        }

        setTimeout(() => {
          component.performMaskContains = true
        }, 300)
      })
    })
  }

  render() {
  return ( 
      <MapDiv id="map">
        <MapLoadingSpinner visible={this.state.mapViewUpdating} />  
          {this.state.featuresFound === false && (
        <MapOverlayControl>No features were found at this location</MapOverlayControl>  
         )}
      </MapDiv>
    )
  }
  }
     
export default MapControl
